/**
 * FollowActorAction - behavior tree action for following a target actor.
 */

import * as THREE from 'three';

import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface FollowActorActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  followDistance?: number;
  stopDistance?: number;
  continueAfterReached?: boolean;
  pathRecalculationDistance?: number;
}

@EngineClass('FollowActorAction')
export class FollowActorAction extends BehaviorAction {
  private targetActorKey?: string;
  private stopDistance: number;
  private continueAfterReached: boolean;
  private movementComponent: NpcMovementComponent | null = null;

  constructor(options: FollowActorActionOptions = {}) {
    super({
      name: 'FollowActor',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.stopDistance = options.stopDistance ?? 2.0;
    this.continueAfterReached = options.continueAfterReached ?? true;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(NpcMovementComponent);
    if (!this.movementComponent) {
      console.warn('FollowActorAction: No NpcMovementComponent found on actor');
      return;
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return;
    }

    // Determine target actor
    let targetActor: Actor | undefined;
    if (this.targetActorKey) {
      targetActor = blackboard.getValue<Actor>(this.targetActorKey);
    }

    if (!targetActor) {
      console.warn('FollowActorAction: No target actor specified');
      return;
    }

    // Use the movement component's new method - it handles all pathfinding internally
    this.movementComponent.setTargetActor(targetActor, this.stopDistance, this.continueAfterReached);
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Get target actor (might have changed in blackboard)
    let targetActor: Actor | undefined;
    if (this.targetActorKey) {
      targetActor = blackboard.getValue<Actor>(this.targetActorKey);
    }

    if (!targetActor) {
      return BehaviorStatus.Failure;
    }

    // Update the target actor in case it changed
    const currentFollowedActor = this.movementComponent.getFollowedActor();
    if (currentFollowedActor !== targetActor) {
      this.movementComponent.setTargetActor(targetActor, this.stopDistance, this.continueAfterReached);
    }

    // Get current positions
    const ownerPosition = new THREE.Vector3();
    const targetPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    targetActor.rootComponent.getWorldPosition(targetPosition);

    // Calculate distance to target
    const distanceToTarget = ownerPosition.distanceTo(targetPosition);

    // Check if we're within stop distance of the target
    if (distanceToTarget <= this.stopDistance) {
      if (!this.continueAfterReached) {
        // We're close enough to the target - complete the action
        return BehaviorStatus.Success;
      }
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Clear the actor following when exiting the action
    // This prevents interference with other movement actions
    this.movementComponent?.setTargetActor(null);

    // Only stop movement inputs, don't clear the path
    // This allows the next action to continue using the existing path if it's still valid
    this.movementComponent?.stopMovement();
  }
}
